import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError } from '@src/helpers/toastError'
import { PayloadCreatorParams } from '@src/redux/types'
import { omit } from 'ramda'

import * as api from '../api'
import {
  HELP_DESK_TICKETS,
  HELP_DESK_TICKETS_ALL,
  HELP_DESK_TICKETS_CREATE,
  HELP_DESK_TICKETS_DELETE,
  HELP_DESK_TICKETS_LIST,
  HELP_DESK_TICKETS_OWN,
  HELP_DESK_TICKETS_UPDATE,
} from '../constants/actionTypes'
import * as API from '../constants/api'

export const helpDeskTickets = createAsyncThunk(
  HELP_DESK_TICKETS,
  async (params: PayloadCreatorParams<`${typeof API.HELP_DESK_TICKETS}{id}`, 'get'>, { rejectWithValue }) => {
    try {
      const response = await api.helpDeskTickets(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const helpDeskTicketsList = createAsyncThunk(
  HELP_DESK_TICKETS_LIST,
  async (params: PayloadCreatorParams<`${typeof API.HELP_DESK_TICKETS}`, 'get'> | undefined, { rejectWithValue }) => {
    try {
      const response = await api.helpDeskTicketsList(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const helpDeskTicketsOwn = createAsyncThunk(
  HELP_DESK_TICKETS_OWN,
  async (
    params: PayloadCreatorParams<`${typeof API.HELP_DESK_TICKETS_OWN}`, 'get'> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.helpDeskTicketsOwn(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const helpDeskTicketsAll = createAsyncThunk(
  HELP_DESK_TICKETS_ALL,
  async (
    params: PayloadCreatorParams<`${typeof API.HELP_DESK_TICKETS_ALL}`, 'get'> | undefined,
    { rejectWithValue }
  ) => {
    try {
      const response = await api.helpDeskTicketsAll(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const helpDeskTicketsCreate = createAsyncThunk(
  HELP_DESK_TICKETS_CREATE,
  async (params: PayloadCreatorParams<`${typeof API.HELP_DESK_TICKETS}`, 'post'>, { rejectWithValue }) => {
    try {
      const response = await api.helpDeskTicketsCreate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const helpDeskTicketsDelete = createAsyncThunk(
  HELP_DESK_TICKETS_DELETE,
  async (params: PayloadCreatorParams<`${typeof API.HELP_DESK_TICKETS}{id}`, 'delete'>, { rejectWithValue }) => {
    try {
      const response = await api.helpDeskTicketsDelete(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const helpDeskTicketsUpdate = createAsyncThunk(
  HELP_DESK_TICKETS_UPDATE,
  async (params: PayloadCreatorParams<`${typeof API.HELP_DESK_TICKETS}`, 'put'>, { rejectWithValue }) => {
    try {
      const response = await api.helpDeskTicketsUpdate(omit(['onFulfilled', 'onRejected', 'onSettled'], params))
      params?.onFulfilled && params.onFulfilled(response.data)
      return response.data
    } catch (e) {
      const error = toastError(e)
      params?.onRejected && params.onRejected({ error })
      return rejectWithValue(error)
    } finally {
      params?.onSettled && params.onSettled()
    }
  }
)

export const helpDeskTicketsClear = createAction(`${HELP_DESK_TICKETS}/clear`)

export const helpDeskTicketsListClear = createAction(`${HELP_DESK_TICKETS_LIST}/clear`)

export const helpDeskTicketsAllClear = createAction(`${HELP_DESK_TICKETS_ALL}/clear`)

export const helpDeskTicketsOwnClear = createAction(`${HELP_DESK_TICKETS_OWN}/clear`)
